/**
 * Web port of ios/zubi/Shared/Helpers/TagTheme.swift — deterministic tag
 * colors shared with the native apps. Hue = FNV-1a(tag) % 360 (tags are
 * stored lowercased on every platform; we lowercase defensively). The
 * saturation/lightness pairs are the HSL equivalents of macOS's
 * HSB(0.45, 0.90) dark / HSB(0.55, 0.85) light.
 */
const FNV_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const MASK_64 = (1n << 64n) - 1n;

const fnv1aHash = (str: string): bigint => {
  let hash = FNV_OFFSET_BASIS;
  for (const byte of new TextEncoder().encode(str)) {
    hash ^= BigInt(byte);
    hash = (hash * FNV_PRIME) & MASK_64;
  }
  return hash;
};

export type ColorScheme = 'light' | 'dark';

export const tagHue = (tagName: string): number =>
  Number(fnv1aHash(tagName.toLowerCase()) % 360n);

const HSL_BY_SCHEME: Record<ColorScheme, { s: number; l: number }> = {
  dark: { s: 66.9, l: 69.8 },
  light: { s: 60.9, l: 61.6 },
};

export const tagColor = (tagName: string, scheme: ColorScheme): string => {
  const { s, l } = HSL_BY_SCHEME[scheme];
  return `hsl(${tagHue(tagName)}, ${s}%, ${l}%)`;
};

export const tagBackgroundColor = (tagName: string, scheme: ColorScheme): string => {
  const { s, l } = HSL_BY_SCHEME[scheme];
  return `hsla(${tagHue(tagName)}, ${s}%, ${l}%, 0.15)`;
};
